import { Spinner } from '@change/ds-core-ui/Spinner';

import { useKnownAvailableParams } from '@change-corgi/core-react/router';

import { withPrefetchedData } from '#src/shared/prefetch';

import {
	PetitionGuidesCategoryPageContextProvider,
	prefetchPageContext,
} from '#src/app/pages/petitionGuides/category/details/pageContext';
import type { PetitionGuidesCategoryPagePrefetchedDataProps } from '#src/app/pages/petitionGuides/category/details/pageContext';
import { PAGE_SIZE } from '#src/app/pages/petitionGuides/category/details/shared/consts';
import { getPetitionGuidesCategoryPageTitle } from '#src/app/pages/petitionGuides/category/details/shared/utils';
import { isLoaded } from '#src/app/shared/utils/async';

import { Layout } from '../layout';

import { PetitionGuidesCategoryErrorHandler } from './ErrorHandler';

/** Page 1 is canonicalized without a query string; page N>1 carries `?page=N`. */
function getPageQuerySuffix(page: number): string {
	return page > 1 ? `?page=${page}` : '';
}

export const PetitionGuidesCategory = withPrefetchedData(
	function PetitionGuidesCategory({ prefetchedData }: PetitionGuidesCategoryPagePrefetchedDataProps) {
		const { categorySlug } = useKnownAvailableParams<{ categorySlug: string }>();

		return (
			<PetitionGuidesCategoryPageContextProvider
				slug={categorySlug}
				prefetchedData={prefetchedData}
				errorHandler={PetitionGuidesCategoryErrorHandler}
				loadingFallback={<Spinner className="mx-auto my-64" />}
			>
				<Layout />
			</PetitionGuidesCategoryPageContextProvider>
		);
	},
	{
		prefetchName: __MODULE_HASH__,

		// oxlint-disable-next-line typescript-eslint/no-non-null-assertion
		prefetchData: async (context) => prefetchPageContext(context.params.categorySlug!, context),
		pageTitle: ({ prefetchedData, fromTemplate, i18n }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			return fromTemplate(getPetitionGuidesCategoryPageTitle(prefetchedData.pageData.pageData, i18n));
		},
		pageCanonical: ({ prefetchedData }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			const { slug, page } = prefetchedData.pageData.pageData;
			return `/petition-guides/category/${slug}${getPageQuerySuffix(page)}`;
		},
		pageMetadata: ({ prefetchedData, i18n }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			const { seoTitle, seoDescription, name, introParagraph, articles } = prefetchedData.pageData.pageData;
			const title = seoTitle ?? name ?? i18n.translate('corgi.petition_guides_category_page.meta.title');
			const description =
				seoDescription ?? introParagraph ?? i18n.translate('corgi.petition_guides_category_page.meta.description');

			return {
				description,
				ogTitle: title,
				ogDescription: description,
				ogType: 'website' as const,
				...(articles.length === 0 && { robots: 'noindex' as const }),
			};
		},
		pageSchemaOrg: ({ prefetchedData, i18n, defineBreadcrumb, defineItemList, defineListItem, resolveUrl }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}

			const { articles, name, page, totalArticleCount } = prefetchedData.pageData.pageData;
			const resolvedName = name ?? i18n.translate('corgi.petition_guides_category_page.page_heading.title');

			return [
				defineItemList({
					name: resolvedName,
					numberOfItems: totalArticleCount,
					itemListElement: articles.map(({ heading, slug }, index) =>
						defineListItem({
							position: (page - 1) * PAGE_SIZE + index + 1,
							url: resolveUrl(`/petition-guides/${slug}`),
							name: heading,
						}),
					),
				}),
				defineBreadcrumb({
					itemListElement: [
						{
							name: i18n.translate('corgi.petition_guides_category_page.breadcrumbs.home'),
							item: resolveUrl('/'),
						},
						{
							name: i18n.translate('corgi.petition_guides_category_page.breadcrumbs.guides'),
							item: resolveUrl('/petition-guides'),
						},
						{ name: resolvedName },
					],
				}),
			];
		},
	},
);
